import styled, { css } from 'styled-components';

export const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.45;
`;

export const Step = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #ffffff;

  & + & {
    margin-top: 14px;
  }
`;

export const StepHead = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;

  .badge {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: #e6f3ec;
    color: #006633;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }
`;

export const StepHint = styled.p`
  margin: -4px 0 4px 36px;
  font-size: 12.5px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.45;
`;

export const TextInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13.5px;
  color: #0f172a;
  background: #ffffff;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #006633;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
  }
`;

export const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

export const TypeCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: all 0.15s ease;

  ${({ $selected }) =>
    $selected
      ? css`
          border: 1px solid #006633;
          background: #f1faf4;
          box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.1);
        `
      : css`
          border: 1px solid #e2e8f0;

          &:hover {
            border-color: #c7e4d5;
            background: #fafbfc;
          }
        `}

  .head {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${({ $selected }) => ($selected ? '#006633' : '#475569')};
  }

  .head strong {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  small {
    font-size: 11.5px;
    color: #64748b;
    font-weight: 500;
    line-height: 1.4;
  }
`;

export const TypeMarker = styled.span<{
  $variant: 'radio' | 'checkbox';
  $selected: boolean;
}>`
  width: 16px;
  height: 16px;
  border-radius: ${({ $variant }) => ($variant === 'radio' ? '50%' : '4px')};
  border: 2px solid ${({ $selected }) => ($selected ? '#006633' : '#cbd5e1')};
  background: ${({ $selected }) => ($selected ? '#006633' : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  ${({ $selected, $variant }) =>
    $selected &&
    $variant === 'radio' &&
    css`
      &::after {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #ffffff;
      }
    `}

  ${({ $selected, $variant }) =>
    $selected &&
    $variant === 'checkbox' &&
    css`
      &::after {
        content: '';
        width: 8px;
        height: 4px;
        border-left: 2px solid #ffffff;
        border-bottom: 2px solid #ffffff;
        transform: rotate(-45deg) translate(1px, -1px);
      }
    `}
`;

export const AnswersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AnswerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input {
    flex: 1;
    height: 38px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 13px;
    color: #0f172a;
    background: #ffffff;
    font-family: inherit;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
      color: #94a3b8;
    }

    &:focus {
      outline: none;
      border-color: #006633;
      box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
    }
  }
`;

export const OrderBadge = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11.5px;
  font-weight: 700;
  flex-shrink: 0;
`;

export const RemoveAnswer = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #fecaca;
    color: #b91c1c;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const AddAnswerButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 9px;
  border: 1px dashed #c7e4d5;
  background: #f1faf4;
  color: #006633;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
  margin-top: 2px;

  &:hover {
    background: #e6f3ec;
    border-style: solid;
  }
`;

export const FooterHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #94a3b8;
  }
`;

export const FooterButton = styled.button<{
  $variant?: 'primary' | 'secondary';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;

  ${({ $variant = 'primary' }) =>
    $variant === 'primary'
      ? css`
          background: #006633;
          color: #ffffff;
          border: 1px solid #006633;

          &:hover:not(:disabled) {
            background: #00451f;
          }
        `
      : css`
          background: #ffffff;
          color: #0f172a;
          border: 1px solid #e2e8f0;

          &:hover:not(:disabled) {
            background: #f8fafc;
            border-color: #006633;
            color: #006633;
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
